import { ENV } from "~/ENV"
import { readerUrl } from "~/features/library/readerUrl"
import { EntitlementCategory } from "~/graphql/generated/graphql"

/**
 * Where a seat's content actually lives, worked out from the row itself instead
 * of handed off to myLifeway's `/redirect` page.
 *
 * ---------------------------------------------------------------------------
 * WHY NOT THE REDIRECT
 * ---------------------------------------------------------------------------
 *
 * `resourceRedirectUrl` builds `${MLC_ORIGIN}/redirect?itemNumber={lin}`, and
 * that is byte-identical to what myLifeway's own buttons use — `pathHelpers.js`
 * `resourceRedirectQuery` appends the item number and nothing else, confirmed by
 * reading it. So the URL is not malformed; the problem is what happens on the
 * other end.
 *
 * `ExternalResourceRedirectPage` dispatches `getResourcesPending(itemNumber)`,
 * which is `mediaContentLogic.js#getResourceURLs`: a **client-side** `GET
 * /api-my/resources/{lin}/locations` carrying the signed-in user's token, then
 * `window.location.replace(locations[0].url)`. Cold, in a tab with no myLifeway
 * session, there is no token to send — so the customer lands on sign-in, or on
 * the error banner, rather than on their content. Every link the Digital Library
 * hands out is a cold link by definition; it is a different origin.
 *
 * ---------------------------------------------------------------------------
 * WHAT REPLACES IT, AND ON WHAT EVIDENCE
 * ---------------------------------------------------------------------------
 *
 * `/locations` was resolved for all 68 seats on the UAT account and the answers
 * are a function of the distribution channel and the item number, with no
 * counterexamples:
 *
 * | channel        | n  | destination                              |
 * |----------------|----|------------------------------------------|
 * | `LRA/MSB/WSA`  | 30 | `read.lifeway.com/reader/{lin}`           |
 * | `VLC/VOD`      | 24 | `player.lifeway.com/player/{lin}`         |
 * | `LLC`          |  3 | `live.lifeway.com` (constant, no lin)     |
 * | `DGP`          |  1 | `digitalpass.lifeway.com/my-dashboard` *   |
 * | `SMG`          |  1 | `smallgroup.com`                         |
 * | `CLC`          |  1 | `ministrygrid.lifeway.com`               |
 *
 * These are the destinations UAT itself returns, and they are **prod** hosts —
 * `/locations` on UAT hands back `read.lifeway.com`, not a UAT reader.
 *
 * \* `DGP` is the one row we do NOT take at its word, and not for the
 * environment reason below: there is no Digital Pass web front end any more, so
 * that dashboard URL resolves to nothing a customer can use. Digital Pass seats
 * are watched on Lifeway Live, so `DGP` is folded into the `LLC` rule. Commerce
 * has not caught up with a retired front end — this is stale data on their side,
 * not a channel we failed to recognise.
 *
 * So they are the DEFAULTS and not constants. Taking commerce at its word sends
 * a UAT tester to prod, where the seat they just redeemed does not exist and the
 * reader asks them to sign in to an account that does not have it. The three
 * Lifeway-owned destinations therefore come from `ENV` — `READER_URL` already
 * existed and already points at the non-prod reader for the Ebooks cards, and
 * this module was the one place still hardcoding prod past it — falling back to
 * the observed prod host when unset. Overriding commerce is deliberate: what
 * `/locations` returns is right for production and wrong for every other
 * environment.
 *
 * A derived link works in a cold tab because each of those destinations does its
 * own authentication. That is the entire gain.
 *
 * ---------------------------------------------------------------------------
 * WHAT IS DELIBERATELY NOT DERIVED
 * ---------------------------------------------------------------------------
 *
 * **Downloads.** `FILE_DOWNLOAD` resolves to a presigned CloudFront URL —
 * `download.prod.lifeway.com/products/{lin}/purchased_downloads/{lin}.zip` with
 * `Expires` and `Signature` — and the observed one expired **within the hour**.
 * It cannot be derived, and it must not be resolved early either: a URL fetched
 * when the page loaded would be dead by the time anyone clicked it. Resolving at
 * click time is exactly right for these, so downloads keep the redirect. That is
 * the mechanism working, not a gap.
 *
 * **Anything unrecognised.** A row whose channel codes match nothing gets no
 * derived link and falls back to the redirect, which is what myLifeway does with
 * it too. Guessing a host from a code this build has never seen would turn an
 * honest error banner into a wrong destination.
 *
 * ---------------------------------------------------------------------------
 * THIS IS POC INFERENCE, NOT A CONTRACT
 * ---------------------------------------------------------------------------
 *
 * Commerce owns these destinations and can change them without telling us; 68
 * seats on one account is evidence, not a guarantee. Production should carry the
 * target on the row — `accessTargets` as a lazy field is already resolvable by
 * the facade through the same `/locations` call (proposal 03 §4). This module
 * exists so the POC can demo a link that actually lands, and so the shape of
 * the eventual field is written down. See FINDINGS.md gap 32.
 */

/**
 * SmallGroup and Ministry Grid stay constants: they are separate products with
 * their own environments, this app has no configured origin for either, and
 * inventing a non-prod hostname for them would be the guess the module's whole
 * premise refuses to make.
 */
const SMALLGROUP = "https://www.smallgroup.com"
/**
 * The login route, not the bare origin — that is what `/locations` returns, and
 * the difference is invisible to any check that compares URL paths, because it
 * lives in the fragment. Caught only by diffing derived against actual per row.
 */
const MINISTRY_GRID = "https://ministrygrid.lifeway.com/#/login"

/** The Lifeway-owned destinations, per environment. */
export interface AccessOrigins {
  readonly reader: string
  readonly player: string
  readonly liveEvents: string
}

export const ENV_ACCESS_ORIGINS: AccessOrigins = {
  reader: ENV.READER_URL,
  player: ENV.PLAYER_URL,
  liveEvents: ENV.LIVE_EVENTS_URL,
}

const origin = (url: string): string => url.replace(/\/+$/, "")

interface ChannelRule {
  readonly codes: readonly string[]
  readonly href: (itemNumber: string, origins: AccessOrigins) => string
  /**
   * Where the link goes, named. MLC's Access column says "Lifeway on demand" for
   * everything and puts the real distinction behind a tooltip; naming the actual
   * destination is the same information without the hover, and it is derived
   * from the same channel codes as the href — so the label can never disagree
   * with where the button lands.
   */
  readonly label: string
}

/**
 * Ordered, because a seat carries several codes at once — every ebook on the UAT
 * account is `[LRA, MSB, WSA]`. First match wins.
 */
const CHANNEL_RULES: readonly ChannelRule[] = [
  { codes: ["SMG"], href: () => SMALLGROUP, label: "SmallGroup.com" },
  { codes: ["CLC", "TMG"], href: () => MINISTRY_GRID, label: "Ministry Grid" },
  {
    /**
     * `readerUrl`, not a local join — the Ebooks cards already build reader
     * links with it, and two functions producing reader URLs is how the seat
     * link and the card link end up disagreeing. It is keyed by item number
     * here and by content GUID there, which is the missing-join-key problem
     * (proposal 01) and not a difference in the URL shape.
     */
    codes: ["LRA", "MSB", "WSA"],
    href: (item, origins) => readerUrl(item, origins.reader),
    label: "Lifeway Reader",
  },
  {
    codes: ["VLC", "VOD"],
    href: (item, origins) => `${origin(origins.player)}/player/${encodeURIComponent(item)}`,
    label: "Lifeway On Demand",
  },
  /**
   * `DGP` rides with `LLC`. Commerce resolves a Digital Pass seat to
   * `digitalpass.lifeway.com/my-dashboard`, which no longer has a front end
   * behind it; the content is watched on Lifeway Live like every other live
   * seat. Following `/locations` here would send a customer to a dead host.
   */
  { codes: ["LLC", "DGP"], href: (_item, origins) => origin(origins.liveEvents), label: "Lifeway Live" },
]

const ruleFor = (channelCodes: readonly string[] | null | undefined): ChannelRule | undefined => {
  const codes = channelCodes ?? []

  return CHANNEL_RULES.find(candidate => candidate.codes.some(code => codes.includes(code)))
}

/**
 * Downloads are excluded by category rather than by channel: the one on the UAT
 * account carries no channel codes at all, so nothing would have matched anyway,
 * but the exclusion needs to hold for a download that does carry them.
 */
const RESOLVED_AT_CLICK_TIME: readonly EntitlementCategory[] = [EntitlementCategory.Download]

/**
 * The seat's destination, or undefined when this build cannot say — in which case
 * the caller should fall back to myLifeway's redirect rather than invent one.
 */
export const accessTargetFor = (
  category: EntitlementCategory,
  channelCodes: readonly string[] | null | undefined,
  itemNumber: string | null | undefined,
  origins: AccessOrigins = ENV_ACCESS_ORIGINS,
): string | undefined => {
  if (!itemNumber || RESOLVED_AT_CLICK_TIME.includes(category)) {
    return undefined
  }

  return ruleFor(channelCodes)?.href(itemNumber, origins)
}

/**
 * The destination, named, for MLC's Access column.
 *
 * Downloads are named rather than left blank: they have no derived href because
 * the URL is signed and short-lived, but "Download" is still the honest answer
 * to "where does this go", and a blank cell reads as missing data.
 */
export const accessDestinationLabel = (
  category: EntitlementCategory,
  channelCodes: readonly string[] | null | undefined,
): string | undefined => {
  if (category === EntitlementCategory.Download) {
    return "Download"
  }

  return ruleFor(channelCodes)?.label
}
